import { Container } from "@bryntum/schedulerpro";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { getIsArchivedForBoard } from "../../../other/utils/otherUtils.jsx";
import { getSavedBoardSelection } from "../../../react/hooks/useBoards.jsx";
import { lineThroughString, showConfirmationDialog } from "../../../other/utils/utils.jsx";

class DuplicateEntityHelper extends Container {
  static get $name() {
    return "DuplicateEntityHelper";
  }

  static get type() {
    return WIDGET_KEYS.duplicateEntityHelper;
  }

  construct(...args) {
    super.construct(...args);

    const commonFieldConfig = {
      name: this.name,
      displayField: this.name,
      valueField: this.name,
      labelWidth: this.labelWidth,
      label: this.label,
      placeholder: this.placeholder,
      autocomplete: this.autocomplete,
      maxLength: this.maxLength,
    };

    const config = [
      {
        ref: "swivuCombo",
        type: "combo",
        validateFilter: false,
        filterOperator: "*",
        listeners: {
          select: this.onComboFieldSelect.bind(this),
          focusOut: this.onComboFieldFocusOut.bind(this),
        },
        listItemTpl: this.comboListItemTpl.bind(this),
        createOnUnmatched: this.createOnUnmatched.bind(this),
        ...commonFieldConfig
      },
      {
        ref: "swivuInput",
        type: "text",
        listeners: {
          change: this.onInputFieldChange.bind(this)
        },
         hidden: true,
        ...commonFieldConfig
      }
    ]

    this.add(config)
  }

  /* Some external functions/widgets may use duplicateEntityHelper.value to access value of the field, but duplicate entity helper is container so value connot be accessed. Thus this getter exposes the value to external widgets. e.g 'onClickSaveBtn' function in MainEditorBase.jsx */
  get value() {
    return this.swivuTabContainer.record[this.name];
  }

  /* Since, we have a getter to access value of duplicateEntityHelper, we provided a setter as well to keep duplicate entity helper consistent */
  set value(val) {
    this.swivuTabContainer.record[this.name] = val;
  }

  loadRecord(record, context, otherParams) {
    if (!this.isFieldInitialized) {
      const { editor, onAfterFieldUpdate, recordContainer } = otherParams;
      const { swivuCombo } = this.widgetMap;
      this.project = context.project

      this.swivuOriginalRecord = record;
      this.swivuTabContainer = recordContainer;
      this.swivuEditor = editor;
      this.swivuStore = context.project.getCrudStore(this.storeKey);
      this.onAfterFieldUpdate = onAfterFieldUpdate;
      if (record.isPhantom) {
        this.handleStoreListeners();
        swivuCombo.store.add(this.getItemRecords());
      } else {
        this.showInput()
      }
      this.isFieldInitialized = true;
    }
  }

  onComboFieldSelect({ record }) {
    this.isLoadingRecord = true;
    this.showInput();
    if (record === this.swivuOriginalRecord) {
      this.widgetMap.swivuInput.value = record[this.name];
    } else {
      this.loadEditor(record);
      this.showWarningDialog({ record });
    }
    this.isLoadingRecord = false;
  }

  onComboFieldFocusOut({ source }) {
    // Applying `isLoadingRecord` because when input field is loaded and combo is destroyed it also triggers a focus out listener
    if (!this.isLoadingRecord && this.isFieldInitialized) {
      const value = source.input.value.trim();
      if (value) {
        const matchedRecord = this.swivuStore.find((record) => record[this.name] === value);
        if (matchedRecord) {
          this.onComboFieldSelect({ record: matchedRecord });
        } else {
          const newRecord = this.createOnUnmatched(value);
          this.onComboFieldSelect({ record: newRecord });
        }
      }
    }
  }

  onInputFieldChange(e) {
    if (this.isFieldInitialized) {
      const { source, value } = e;
      if (value && value.trim() !== "") {
        // Trigger on change on container, handle editor title update
        this.onAfterFieldUpdate.call(this.swivuTabContainer, {...e, userAction: e.userAction || this.isLoadingRecord});
      } else {
        source.value = this.value;
      }
    }
  }

  showInput() {
    const { swivuInput } = this.widgetMap;
    swivuInput.hidden = false;
    this.hideCombo();
  }

  hideCombo() {
    const { swivuCombo } = this.widgetMap;
    /* Hide combo maybe called through a listener when records idChange therefore checking if the combo is destroyed before removing it */
    if (swivuCombo && !swivuCombo.isDestroyed) {
      /* Container is calling eachWidget and setting value for it once it was registered inside it, so if we destroy the combo directly the container won't be able to access it anymore, therefore we need to remove the combo first. */
      this.remove(swivuCombo);
      /* We are using a timeout here because some 'keyevent' listeners inside combo use its instance so if we destroy the combo right away before those callbacks are resolved we encounter issues. */
      setTimeout(() => {
        swivuCombo.destroy(); // Combo is not needed anymore after the input is shown
      })
    }
  }

  getItemRecords() {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
    return this.swivuStore.query((record) => record[this.name] && !record.isLinked && (canShowArchivedRecords || !record.archived), true);
  }

  createOnUnmatched(name) {
    this.swivuTabContainer.record[this.name] = name;
    /* Bug: If we don't set the project here and create new record from duplicate entity helper by opening editor from dragging on the scheduler widget, the project will be undefined when new events are created.
    Explanation: We are assigning project to record since it's phantom and its first store is this combo's store, so if any function tries to access entity by getters or fields it will return instance assigned to this combo. since the first store will be this combo. Here is where the problem lies, if the first store changes and record is phantom then it does not have the project property so any getters in the entity model will not be able to access project.  */
    this.swivuTabContainer.record.project = this.swivuTabContainer.project;
    return this.swivuTabContainer.record;
  }

  loadEditor(entityRecord) {
    this.swivuSelectedRecord = entityRecord;
    const { editorHandler } = this.swivuEditor.context;
    const editorReactState = this.swivuEditor.reactState;
    const existingOpenedEditor = editorHandler.getEditors().find((editor) => {
      return (editor.id !== editorReactState.id && editor.type === editorReactState.type && editor.recordIdObj.id === entityRecord.id);
    });
    /* Here we are updating editor's react before hiding existing editor
    Because hiding previous editors trigger re render therefore, the record in state is not updated for this editor, this causes the current editor to not load properly */
    this.swivuEditor.updateReactState(entityRecord);
    if (existingOpenedEditor) {
      existingOpenedEditor.editorRef.hide();
    }
    entityRecord.isDuplicateEntityHelperRecord = true;
    this.swivuEditor.editRecord(entityRecord, this.swivuEditor.context, {
      ...this.swivuEditor.otherParams,
      isFirstShow: true
    });
    delete entityRecord.isDuplicateEntityHelperRecord;
  }

  handleStoreListeners() {
    this.swivuStoreListenersDetacher = this.swivuStore.on({
      /* If two instance of editors are opened and user creates a new record in the store then the second editor is not going to have updated picker list items to handle duplication. */
      add: ({ records: addedRecords }) => {
        if (!addedRecords.map((item) => item.id).includes(this.swivuTabContainer.record.id)) {
          this.widgetMap.swivuCombo.store.add(addedRecords);
        }
      },
      /* If the record is persisted in the editor by other widget than duplicate entity helper, i.e, Company cam tab. Then the record is not persisted in the duplicate entity helper to fix it we are assigning id change listener to store so that it updates duplicate entity helper */
      idChange: ({ record: persistedRecord }) => {
        if (persistedRecord.id === this.swivuTabContainer.record.id) {
          this.showInput();
          this.widgetMap.swivuInput.trigger("change");
          this.swivuStoreListenersDetacher();

          /* Bryntum Bug: If the container's record is phantom when it becomes non phantom & we try to update a field in record for example: record.name = 'new name'. Then the container won't update the field with this new value.
          Therefore we are updating the container's record manually here, so it starts reading updates from record */
          this.swivuTabContainer.record = persistedRecord;
        }
      },
      thisObj: this
    });

    /* Remove store listeners on editor hide */
    this.swivuEditor.on({
      hide: () => {
        if (this.swivuStoreListenersDetacher)
          this.swivuStoreListenersDetacher();
      },
      thisObj: this,
      once: true
    });
  }

  comboListItemTpl(record) {
    return lineThroughString(record[this.name], record.archived);
  }

  showWarningDialog({ record }) {
    const { swivuCurrentUser } = this.project.boardUsersStore;
    const { name } = this.project.boardUsersStore.find((boardUserRecord) => boardUserRecord.userId === swivuCurrentUser.id);
    showConfirmationDialog({
      title: "Auto-Save is active",
      message: `Hey ${name}. You just opened "${record[this.name]}". Watch out. All changes are auto-saved.`,
      isCancelButton: false
    });
  }
}

DuplicateEntityHelper.initClass();
